// =====================================================================================
// INVRT Radar - web console - operator "Assign campaign" panel (Story W4-E)
//
// A campaign is a dataset the operator assigns to any organisations, several at once,
// scoped by all coverage, US states or ZIP codes, optionally narrowed to lead tiers A, B
// and C. One submit calls POST /admin/access/campaign-assignments and renders the server's
// per-organisation answer in plain English; a failed delivery link offers Re-link. The
// same panel revokes the campaign from the selected organisations by calling the existing
// per-subscription revoke route once per organisation.
//
// Every server answer is read fail-closed: a shape the reviewed contract does not name is
// refused and nothing is inferred from it (the readMutationResponse precedent).
// =====================================================================================

import { useMemo, useState, type FormEvent, type ReactNode } from "react";
import { useQuery } from "@tanstack/react-query";
import { EmptyState, ErrorState, LoadingState } from "@/components/States";
import { Button } from "@/components/ui/button";
import { Chip, type ChipSignal } from "@/components/ui/chip";
import { DataTable, type DataTableRow } from "@/components/ui/data-table";
import { Input, Select, Textarea } from "@/components/ui/input";
import { useApiClient } from "@/hooks/useApi";
import { isApiError } from "@/lib/api";
import { readCatalogueKind } from "@/pages/operator/catalogueReader";
import { useOperatorOrganisations } from "@/pages/operator/useOperatorOrganisations";
import { invalidContract, isCampaign, isDateTime, isUuid, readAll, readMutationResponse, readRelinkResponse } from "./AccessSection";
import { TOKEN_COPY, UNHELD_WARNING } from "./relinkCoverage";
import { parseZipText, scopeValid, STATE_LIST, summarise, TIERS, type Scope, type Tier } from "./campaignScope";

const MAX_ORGS = 50;

// ---- the strict answer reader ---------------------------------------------------------------

export type AssignmentStatus = "assigned" | "refused" | "partial" | "skipped";
export interface AssignmentRow {
  org_id: string;
  status: AssignmentStatus;
  subscription?: Record<string, unknown>;
  anchor?: Record<string, unknown>;
  scope_applied?: boolean;
  delivery_held?: boolean;
  token?: string;
  /** The server's own verdict that the run must stop: the partner or a dependency is down, or the outcome is unknown. */
  provider_down?: boolean;
}

const STATUSES: readonly string[] = ["assigned", "refused", "partial", "skipped"];
const ROW_KEYS = new Set(["org_id", "status", "subscription", "anchor", "scope_applied", "delivery_held", "token", "provider_down"]);

const isRecord = (value: unknown): value is Record<string, unknown> => typeof value === "object" && value !== null && !Array.isArray(value);

const isRestricted = (scope: Scope) => !(scope.mode === "all" && (scope.tags?.length ?? 0) === 0);

function readRow(value: unknown, expectedOrg: string, scope: Scope): AssignmentRow | null {
  if (!isRecord(value) || !Object.keys(value).every((key) => ROW_KEYS.has(key))) return null;
  if (value.org_id !== expectedOrg || typeof value.status !== "string" || !STATUSES.includes(value.status)) return null;
  const status = value.status as AssignmentStatus;
  const { subscription, anchor, scope_applied: scopeApplied, delivery_held: deliveryHeld, token, provider_down: providerDown } = value;
  if (providerDown !== undefined && typeof providerDown !== "boolean") return null;
  if (subscription !== undefined && !(isRecord(subscription) && isUuid(subscription.id))) return null;
  if (anchor !== undefined && !(isRecord(anchor) && typeof anchor.anchored === "boolean")) return null;
  if (scopeApplied !== undefined && typeof scopeApplied !== "boolean") return null;
  if (deliveryHeld !== undefined && (typeof deliveryHeld !== "boolean" || status !== "partial")) return null;
  if (token !== undefined && !(typeof token === "string" && token.length >= 1 && token.length <= 100)) return null;
  // An assigned row is fully done: anchored, no token, and (L4) under a restricted scope the
  // server must have CONFIRMED the coverage was applied. Silence is not confirmation.
  if (status === "assigned" && (subscription === undefined || !isRecord(anchor) || anchor.anchored !== true || token !== undefined || scopeApplied === false || (isRestricted(scope) && scopeApplied !== true))) return null;
  if (status === "partial" && (subscription === undefined || token === undefined)) return null;
  if ((status === "refused" || status === "skipped") && (token === undefined || subscription !== undefined)) return null;
  return value as unknown as AssignmentRow;
}

/** Fail-closed: the answer must name exactly the requested organisations, in request order. */
export function readAssignmentResponse(value: unknown, requested: readonly string[], scope: Scope): AssignmentRow[] | null {
  if (!isRecord(value) || Object.keys(value).length !== 2 || typeof value.correlation_id !== "string" || value.correlation_id.length === 0) return null;
  if (!Array.isArray(value.results) || value.results.length !== requested.length) return null;
  if (new Set(requested).size !== requested.length) return null;
  const rows: AssignmentRow[] = [];
  for (let index = 0; index < requested.length; index += 1) {
    const row = readRow(value.results[index], requested[index]!, scope);
    if (!row) return null;
    rows.push(row);
  }
  return rows;
}

// ---- plain-English copy ---------------------------------------------------------------------


const OUTCOME: Record<AssignmentStatus, { label: string; signal: ChipSignal }> = {
  assigned: { label: "Assigned", signal: "buy" },
  partial: { label: "Needs attention", signal: "pace" },
  refused: { label: "Refused", signal: "risk" },
  skipped: { label: "Skipped", signal: "ctx" },
};


/**
 * HIGH-INTENT trouble. The SERVER decides: every row carries provider_down (the partner or a
 * dependency is down, the outcome is unknown, or the organisation was skipped), computed with
 * the same rule that makes the server skip the rest of a multi-organisation request. The
 * console, which sends one organisation at a time, stops when it is true and lists the rest as
 * not sent. The token tests below stay as a net for an answer from a server that predates the
 * flag (provider_down absent); a present false is never second-guessed upwards by them.
 */
function signalsPartnerTrouble(row: AssignmentRow): boolean {
  if (row.provider_down !== undefined) return row.provider_down;
  const token = row.token ?? "";
  return token === "campaign_anchor_failed" || token.includes("provider_unavailable") || token.includes("provider_timeout") || token.includes("deadline_exceeded");
}

function detailFor(row: AssignmentRow, scope: Scope): ReactNode {
  if (row.status === "assigned") {
    const restricted = !(scope.mode === "all" && (scope.tags?.length ?? 0) === 0);
    return restricted ? `Subscribed and delivery link attached. Coverage applied: ${summarise(scope)}.` : "Subscribed and delivery link attached. All coverage, every tier.";
  }
  const token = row.token ?? "";
  if (row.status === "partial" && row.delivery_held === true && TOKEN_COPY[token]) {
    return <span>{TOKEN_COPY[token]} Delivery is held until coverage is applied.</span>;
  }
  if (token === "assignment_scope_unconfirmed_paused_failed") {
    return <span className="font-semibold text-danger">{TOKEN_COPY[token]}</span>;
  }
  if (row.status === "partial" && row.delivery_held === false && TOKEN_COPY[token]) {
    // A hold the platform could not confirm: say so, never imply the pair is safe.
    return <span className="font-semibold text-danger">{TOKEN_COPY[token]} {UNHELD_WARNING}</span>;
  }
  return (
    <>
      <span>{TOKEN_COPY[token] ?? "This organisation could not be completed. Nothing further was inferred."}</span>
      {TOKEN_COPY[token] ? null : <span className="block font-mono text-xs text-ink-muted">{token}</span>}
    </>
  );
}

// ---- the panel ------------------------------------------------------------------------------

type CoverageMode = "all" | "state" | "zip";
interface ResultView {
  row: AssignmentRow;
  relinked: boolean;
  relinkBusy: boolean;
  relinkError: string | null;
}
interface RevokeView {
  orgId: string;
  outcome: "revoked" | "link_open" | "none" | "changed" | "refused" | "unverified" | "failed";
}

const REVOKE_COPY: Record<RevokeView["outcome"], { label: string; signal: ChipSignal; text: string }> = {
  revoked: { label: "Revoked", signal: "buy", text: "The subscription was revoked and its delivery link closed. History is kept." },
  link_open: { label: "Revoked", signal: "pace", text: "The subscription was revoked, but the delivery link may still be open. Check the organisation's delivery link." },
  none: { label: "None found", signal: "ctx", text: "Nothing to revoke: this organisation does not hold this campaign." },
  changed: { label: "Not confirmed", signal: "risk", text: "This subscription changed after it was read, so nothing was revoked. Try again." },
  refused: { label: "Not confirmed", signal: "risk", text: "The server refused this revoke. Nothing was changed." },
  unverified: { label: "Not confirmed", signal: "risk", text: "The answer could not be verified. Check this organisation's access before retrying." },
  failed: { label: "Not confirmed", signal: "risk", text: "The revoke could not be completed. Nothing was confirmed changed." },
};

export function AssignCampaignPanel() {
  const [open, setOpen] = useState(false);
  return (
    <section aria-label="Assign campaign" className="flex flex-col gap-4 rounded-card border border-line bg-surface p-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="flex flex-col gap-1">
          <h2 className="text-lg font-semibold text-ink">Assign a campaign</h2>
          <p className="max-w-2xl text-sm text-ink-soft">Give one campaign to several organisations in one step, limited to the states, ZIP codes or lead tiers you choose.</p>
        </div>
        <Button type="button" variant="secondary" className="h-11" aria-expanded={open} onClick={() => setOpen((current) => !current)}>
          {open ? "Close assign panel" : "Assign a campaign to organisations"}
        </Button>
      </div>
      {open ? <AssignForm /> : null}
    </section>
  );
}

function AssignForm() {
  const api = useApiClient();
  const organisations = useOperatorOrganisations();
  const campaigns = useQuery({ queryKey: ["operator", "assign", "campaigns"], queryFn: () => readCatalogueKind(api, "campaign"), staleTime: 30_000 });

  const [campaignId, setCampaignId] = useState("");
  const [selected, setSelected] = useState<ReadonlySet<string>>(new Set());
  const [search, setSearch] = useState("");
  const [coverage, setCoverage] = useState<CoverageMode>("all");
  const [states, setStates] = useState<string[]>([]);
  const [zipText, setZipText] = useState("");
  const [tiers, setTiers] = useState<Tier[]>([]);
  const [effectiveFrom, setEffectiveFrom] = useState(() => new Date().toISOString());
  const [effectiveTo, setEffectiveTo] = useState("");
  const [reason, setReason] = useState("");
  const [problem, setProblem] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [results, setResults] = useState<{ views: ResultView[]; scope: Scope; reason: string; names: Map<string, string> } | null>(null);
  const [revokes, setRevokes] = useState<RevokeView[] | null>(null);
  const [confirmRevoke, setConfirmRevoke] = useState(false);
  const [names, setNames] = useState<Map<string, string>>(new Map());

  const active = useMemo(() => (organisations.data ?? []).filter((item) => item.status === "active"), [organisations.data]);
  const shown = useMemo(() => {
    const needle = search.trim().toLowerCase();
    return needle ? active.filter((item) => item.name.toLowerCase().includes(needle)) : active;
  }, [active, search]);
  const campaignOptions = useMemo(() => (campaigns.data ?? []).filter((item) => item.status === "active"), [campaigns.data]);
  const orderedSelection = useMemo(() => active.filter((item) => selected.has(item.id)).map((item) => item.id), [active, selected]);
  const nameOf = (id: string) => names.get(id) ?? active.find((item) => item.id === id)?.name ?? id;

  function toggleOrg(id: string, checked: boolean) {
    setSelected((current) => {
      const next = new Set(current);
      if (checked) next.add(id);
      else next.delete(id);
      return next;
    });
  }

  function buildScope(): Scope | string {
    let base: Scope;
    if (coverage === "all") base = { mode: "all" };
    else if (coverage === "state") {
      if (states.length === 0) return "Choose at least one US state, or choose all coverage. Nothing was sent.";
      base = { mode: "state", states: [...states] };
    } else base = { mode: "zip", zips: parseZipText(zipText) };
    const scope: Scope = tiers.length > 0 ? ({ ...base, tags: [...tiers] } as Scope) : base;
    if (!scopeValid(scope)) return "Enter 1 to 500 unique five-digit ZIP codes, separated by commas or spaces. Nothing was sent.";
    return scope;
  }

  function validate(): { scope: Scope; trimmedReason: string } | string {
    if (!isUuid(campaignId)) return "Choose a campaign. Nothing was sent.";
    if (orderedSelection.length === 0) return "Choose at least one organisation. Nothing was sent.";
    if (orderedSelection.length > MAX_ORGS) return `Choose at most ${MAX_ORGS} organisations per request. Nothing was sent.`;
    const scope = buildScope();
    if (typeof scope === "string") return scope;
    const trimmedReason = reason.trim();
    if (trimmedReason.length < 3 || trimmedReason.length > 500) return "Provide a reason between 3 and 500 characters. Nothing was sent.";
    if (!isDateTime(effectiveFrom)) return "Effective from must be an RFC3339 date-time, for example 2026-10-07T09:00:00.000Z. Nothing was sent.";
    if (effectiveTo && (!isDateTime(effectiveTo) || Date.parse(effectiveTo) <= Date.parse(effectiveFrom))) return "Effective to must be a valid date-time after effective from. Nothing was sent.";
    return { scope, trimmedReason };
  }

  async function onSubmit(event: FormEvent) {
    event.preventDefault();
    if (busy) return;
    const checked = validate();
    if (typeof checked === "string") {
      setProblem(checked);
      return;
    }
    setProblem(null);
    setRevokes(null);
    setConfirmRevoke(false);
    setBusy(true);
    const requested = orderedSelection;
    const snapshot = new Map(requested.map((id) => [id, nameOf(id)] as const));
    // ONE organisation per request, sequentially: the server's start floor lets an organisation
    // begin only while its worst case fits in the request's time, so a multi-organisation request
    // can skip later organisations. One request each gives every organisation the full window.
    // Each result is rendered as it arrives; the loop STOPS on the first answer that does not
    // prove the server finished (a dropped connection, any 5xx, a 408, a broken body) or that
    // refused the request outright (a 4xx would repeat for every organisation).
    setNames(snapshot);
    const done: ResultView[] = [];
    setResults(null);
    for (let index = 0; index < requested.length; index += 1) {
      const orgId = requested[index]!;
      try {
        const response = await api.post<unknown>(
          "/admin/access/campaign-assignments",
          {
            campaign_id: campaignId,
            org_ids: [orgId],
            scope: checked.scope,
            effective_from: effectiveFrom,
            ...(effectiveTo ? { effective_to: effectiveTo } : {}),
            reason: checked.trimmedReason,
          },
          { requiresOrg: false, includeOrg: false },
        );
        const rows = readAssignmentResponse(response, [orgId], checked.scope);
        if (!rows) throw invalidContract("The assignment answer did not match the reviewed contract.");
        done.push({ row: rows[0]!, relinked: false, relinkBusy: false, relinkError: null });
        setResults({ views: [...done], scope: checked.scope, reason: checked.trimmedReason, names: snapshot });
        if (signalsPartnerTrouble(rows[0]!)) {
          const notSent = requested.slice(index + 1).map((id) => snapshot.get(id) ?? id);
          if (notSent.length > 0) {
            setProblem(`The delivery partner reported trouble at ${snapshot.get(orgId) ?? orgId}, so the run stopped. Not sent: ${notSent.join(", ")}. Check the results above, then try the rest again.`);
          }
          break;
        }
      } catch (error) {
        // M2: only a 4xx (other than a request timeout) proves the server refused BEFORE it
        // wrote anything. A dropped connection (0), any 5xx (a gateway timeout included) or a
        // 200 that broke the contract may have committed this organisation already.
        const refused = isApiError(error) && error.httpStatus >= 400 && error.httpStatus < 500 && error.httpStatus !== 408;
        const notSent = requested.slice(index + 1).map((id) => snapshot.get(id) ?? id);
        const rest = notSent.length > 0 ? ` Stopped at ${snapshot.get(orgId) ?? orgId}; not sent: ${notSent.join(", ")}.` : "";
        const assignedAbove = done.filter((view) => view.row.status === "assigned").length;
        const refusedText = index === 0
          ? "The server refused this assignment, so nothing was assigned. Check the campaign, the organisations and the scope, then try again."
          : `This organisation was not assigned. ${assignedAbove} ${assignedAbove === 1 ? "organisation" : "organisations"} above ${assignedAbove === 1 ? "was" : "were"} assigned. Check the campaign, the organisation and the scope, then try again.`;
        setProblem(
          (refused
            ? refusedText
            : "Outcome unknown: some assignments may have been saved. Refresh the Access list before retrying.") + rest,
        );
        break;
      }
    }
    setBusy(false);
  }

  function patchView(orgId: string, patch: (view: ResultView) => ResultView) {
    setResults((current) => (current ? { ...current, views: current.views.map((view) => (view.row.org_id === orgId ? patch(view) : view)) } : current));
  }

  async function relink(view: ResultView) {
    const subscriptionId = view.row.subscription?.id;
    if (!results || typeof subscriptionId !== "string") return;
    const orgId = view.row.org_id;
    const restricted = isRestricted(results.scope);
    patchView(orgId, (current) => ({ ...current, relinkBusy: true, relinkError: null }));
    try {
      // H-1: the re-anchor binds the pair with ALL coverage, so the operator's REQUESTED
      // scope rides with it and the server applies it (or holds the pair).
      const response = await api.post<unknown>(`/admin/access/campaign-subscriptions/${subscriptionId}/anchor`, { reason: results.reason, ...(restricted ? { scope: results.scope } : {}) }, { requiresOrg: false, includeOrg: false });
      const parsed = readRelinkResponse(response, subscriptionId);
      if (!parsed) throw invalidContract("The re-link answer did not match the reviewed contract.");
      patchView(orgId, (current) => {
        const anchored = { anchored: true };
        // MED-B: the server's coverage answer is ALWAYS read, whatever scope was asked. Only a
        // CONFIRMED applied reads as Assigned; anything else (an ALL row whose binding is not
        // visible included) is Needs attention with the server's own token and hold state.
        const coverage = parsed.coverage;
        if (coverage.applied) {
          const { token: _token, delivery_held: _held, scope_applied: _applied, ...rest } = current.row;
          return { ...current, relinked: true, relinkBusy: false, row: { ...rest, status: "assigned" as const, anchor: anchored, ...(restricted ? { scope_applied: true } : {}) } };
        }
        const row: AssignmentRow = { ...current.row, status: "partial", anchor: anchored, scope_applied: false, delivery_held: coverage.held, token: coverage.token };
        return { ...current, relinked: true, relinkBusy: false, row };
      });
    } catch (error) {
      const heldDetail = isApiError(error) ? error.details?.["delivery_held"] : undefined;
      const coverageNote = restricted && heldDetail === true
        ? " Delivery is held until coverage is applied."
        : restricted && heldDetail === false
          ? " Delivery could not be confirmed held, so this organisation may be receiving more than you chose. Check its coverage now."
          : "";
      patchView(orgId, (current) => ({
        ...current,
        relinkBusy: false,
        relinkError: (isApiError(error) && error.httpStatus !== 502 ? "The re-link was refused or the delivery partner was unavailable. Nothing was changed." : "The re-link answer could not be verified. The delivery link may or may not be attached.") + coverageNote,
      }));
    }
  }

  async function revokeAll() {
    const checked = validate();
    // Revoke needs the campaign, the organisations and a reason; the scope does not matter.
    if (!isUuid(campaignId) || orderedSelection.length === 0 || orderedSelection.length > MAX_ORGS || reason.trim().length < 3 || reason.trim().length > 500) {
      setProblem(typeof checked === "string" ? checked : "Choose a campaign, at least one organisation and a reason first. Nothing was changed.");
      setConfirmRevoke(false);
      return;
    }
    setProblem(null);
    setResults(null);
    setConfirmRevoke(false);
    setBusy(true);
    const trimmedReason = reason.trim();
    const requested = orderedSelection;
    setNames(new Map(requested.map((id) => [id, nameOf(id)] as const)));
    const views: RevokeView[] = [];
    for (const orgId of requested) {
      views.push({ orgId, outcome: await revokeOne(orgId, trimmedReason) });
      setRevokes([...views]);
    }
    setBusy(false);
  }

  async function revokeOne(orgId: string, trimmedReason: string): Promise<RevokeView["outcome"]> {
    try {
      const rows = await readAll(api, `/admin/access/organisations/${encodeURIComponent(orgId)}/campaign-subscriptions`, "campaign_subscriptions", isCampaign);
      const targets = rows.filter((row) => row.campaign_id === campaignId && (row.status === "active" || row.status === "suspended"));
      if (targets.length === 0) return "none";
      let outcome: RevokeView["outcome"] = "revoked";
      for (const target of targets) {
        const response = await api.delete<unknown>(`/admin/access/campaign-subscriptions/${target.id}`, { expected_version: target.version, reason: trimmedReason }, { requiresOrg: false, includeOrg: false });
        const parsed = readMutationResponse(response, "campaign", "revoke");
        if (!parsed) return "unverified";
        if (parsed.verdict && !parsed.verdict.ok) outcome = "link_open";
      }
      return outcome;
    } catch (error) {
      if (isApiError(error)) {
        if (error.httpStatus === 409) return "changed";
        if (error.httpStatus === 403) return "refused";
        if (error.httpStatus === 502) return "unverified";
      }
      return "failed";
    }
  }

  const resultRows: DataTableRow[] = (results?.views ?? []).map((view) => {
    const outcome = OUTCOME[view.row.status];
    const canRelink = typeof view.row.subscription?.id === "string" && (view.row.token === "campaign_anchor_failed" || view.row.token === "assignment_scope_skipped_anchor_unbound") && !view.relinked;
    const name = results?.names.get(view.row.org_id) ?? view.row.org_id;
    return {
      id: view.row.org_id,
      org: name,
      outcome: <Chip signal={outcome.signal}>{outcome.label}</Chip>,
      detail: (
        <div className="flex flex-col gap-1">
          <div>{results ? detailFor(view.row, results.scope) : null}</div>
          {view.relinkError ? <p role="alert" className="text-sm text-danger">{view.relinkError}</p> : null}
        </div>
      ),
      action: canRelink ? (
        <Button type="button" size="sm" variant="secondary" className="h-11 min-w-11" disabled={view.relinkBusy} loading={view.relinkBusy} aria-label={`Re-link ${name}`} onClick={() => void relink(view)}>
          Re-link
        </Button>
      ) : null,
    };
  });

  const revokeRows: DataTableRow[] = (revokes ?? []).map((view) => {
    const copy = REVOKE_COPY[view.outcome];
    return { id: view.orgId, org: nameOf(view.orgId), outcome: <Chip signal={copy.signal}>{copy.label}</Chip>, detail: copy.text };
  });

  const campaignName = campaignOptions.find((item) => item.id === campaignId)?.name ?? "this campaign";

  if (organisations.isPending || campaigns.isPending) return <LoadingState label="Loading organisations and campaigns…" />;
  if (organisations.isError) return <ErrorState error={organisations.error} onRetry={() => void organisations.refetch()} />;
  if (campaigns.isError) return <ErrorState error={campaigns.error} onRetry={() => void campaigns.refetch()} />;
  if (active.length === 0) return <EmptyState title="No active organisations" body="Create or reactivate an organisation before assigning a campaign." />;

  return (
    <form className="flex flex-col gap-5" onSubmit={(event) => void onSubmit(event)} aria-label="Assign campaign form" noValidate>
      {busy ? <p role="status" aria-live="polite" className="text-sm text-ink-muted">Working through the organisations. Please wait.</p> : null}
      <Select label="Campaign" className="h-11" value={campaignId} disabled={busy} onChange={(event) => setCampaignId(event.target.value)}>
        <option value="">Choose a campaign</option>
        {campaignOptions.map((item) => (
          <option key={item.id} value={item.id}>{item.name}</option>
        ))}
      </Select>

      <div className="flex flex-col gap-2">
        <Input label="Find organisations" className="h-11" value={search} disabled={busy} onChange={(event) => setSearch(event.target.value)} />
        <div className="flex flex-wrap items-center gap-3">
          <Button type="button" variant="secondary" className="h-11" disabled={busy} onClick={() => setSelected(new Set(active.map((item) => item.id)))}>Select all active organisations</Button>
          <Button type="button" variant="secondary" className="h-11" disabled={busy || selected.size === 0} onClick={() => setSelected(new Set())}>Clear selection</Button>
          <p role="status" className="text-sm text-ink-soft">{orderedSelection.length} {orderedSelection.length === 1 ? "organisation" : "organisations"} selected</p>
        </div>
        <ul className="max-h-64 overflow-y-auto rounded-lg border border-line" aria-label="Organisations">
          {shown.map((item) => (
            <li key={item.id}>
              <label className="flex min-h-11 items-center gap-3 px-3 text-sm text-ink">
                <input type="checkbox" className="h-5 w-5 accent-primary" checked={selected.has(item.id)} disabled={busy} onChange={(event) => toggleOrg(item.id, event.target.checked)} />
                {item.name}
              </label>
            </li>
          ))}
          {shown.length === 0 ? <li className="px-3 py-3 text-sm text-ink-muted">No organisation matches that search.</li> : null}
        </ul>
      </div>

      <div className="flex flex-col gap-3">
        <Select label="Coverage" className="h-11" value={coverage} disabled={busy} onChange={(event) => setCoverage(event.target.value as CoverageMode)}>
          <option value="all">All coverage</option>
          <option value="state">US states</option>
          <option value="zip">ZIP codes</option>
        </Select>
        {coverage === "state" ? (
          <Select label="US states" multiple size={8} className="h-auto" value={states} disabled={busy} hint="Hold Ctrl or Command to choose several." onChange={(event) => setStates(Array.from(event.target.selectedOptions, (option) => option.value))}>
            {STATE_LIST.map((code) => (
              <option key={code} value={code}>{code}</option>
            ))}
          </Select>
        ) : null}
        {coverage === "zip" ? (
          <Textarea label="ZIP codes" value={zipText} disabled={busy} rows={4} hint="Five-digit ZIP codes, up to 500, separated by commas, spaces or new lines. Keep leading zeroes." onChange={(event) => setZipText(event.target.value)} />
        ) : null}
        <fieldset className="flex flex-col gap-2" disabled={busy}>
          <legend className="text-sm font-medium text-ink">Lead tiers (optional)</legend>
          <div className="flex flex-wrap gap-4">
            {TIERS.map((tier) => (
              <label key={tier} className="flex min-h-11 items-center gap-2 text-sm text-ink">
                <input type="checkbox" className="h-5 w-5 accent-primary" checked={tiers.includes(tier)} onChange={(event) => setTiers((current) => (event.target.checked ? [...current, tier] : current.filter((item) => item !== tier)))} />
                Tier {tier}
              </label>
            ))}
          </div>
          <p className="text-xs text-ink-muted">Leave all unticked to include every tier. Coverage and tiers both apply.</p>
        </fieldset>
      </div>

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <Input label="Effective from" className="h-11" value={effectiveFrom} disabled={busy} onChange={(event) => setEffectiveFrom(event.target.value)} hint="RFC3339 offset date-time." />
        <Input label="Effective to (optional)" className="h-11" value={effectiveTo} disabled={busy} onChange={(event) => setEffectiveTo(event.target.value)} hint="Leave empty for open-ended." />
      </div>
      <Input label="Reason" className="h-11" value={reason} disabled={busy} maxLength={500} onChange={(event) => setReason(event.target.value)} />

      {problem ? <p role="alert" className="text-sm text-danger">{problem}</p> : null}

      <div className="flex flex-wrap items-center justify-end gap-3 border-t border-line pt-4">
        {confirmRevoke ? (
          <>
            <p className="mr-auto text-sm text-ink">Revoke {campaignName} from {orderedSelection.length} {orderedSelection.length === 1 ? "organisation" : "organisations"}? History is kept and each delivery link is closed.</p>
            <Button type="button" variant="secondary" className="h-11" onClick={() => setConfirmRevoke(false)}>Cancel</Button>
            <Button type="button" variant="danger" className="h-11" onClick={() => void revokeAll()}>Confirm revoke</Button>
          </>
        ) : (
          <>
            <Button
              type="button"
              variant="secondary"
              className="h-11"
              disabled={busy}
              onClick={() => {
                const hasInputs = isUuid(campaignId) && orderedSelection.length > 0 && reason.trim().length >= 3;
                if (!hasInputs) setProblem("Choose a campaign, at least one organisation and a reason first. Nothing was changed.");
                else {
                  setProblem(null);
                  setConfirmRevoke(true);
                }
              }}
            >
              Revoke from selected
            </Button>
            <Button type="submit" className="h-11" loading={busy} disabled={busy}>Assign campaign</Button>
          </>
        )}
      </div>

      {results ? (
        <DataTable
          label="Assignment results"
          caption="One row per organisation. A problem with one organisation never affects another."
          columns={[
            { key: "org", label: "Organisation" },
            { key: "outcome", label: "Outcome" },
            { key: "detail", label: "What happened" },
            { key: "action", label: "Action" },
          ]}
          rows={resultRows}
        />
      ) : null}
      {revokes ? (
        <DataTable
          label="Revoke results"
          caption="One row per organisation. A problem with one organisation never affects another."
          columns={[
            { key: "org", label: "Organisation" },
            { key: "outcome", label: "Outcome" },
            { key: "detail", label: "What happened" },
          ]}
          rows={revokeRows}
        />
      ) : null}
    </form>
  );
}
